/* eslint-disable @typescript-eslint/indent */

import { Component, Element, Event, EventEmitter, Fragment, h, Listen, Prop, State } from '@stencil/core';
import { ActionType } from '@gitg/rcm-ip-component-library';
import { ButtonAppearance, ButtonTheme } from '../rcm-button/rcm-button.types';
import { TabEntry } from './rcm-navigation.types';
import { remCalc } from '../../style';
import { AppService, DomService, GlobalConfig, GlobalContent, RCM_NO_USER_TYPE_COUNTRIES, StringService } from '../../services';
import { RcmButton } from '../rcm-button/rcm-button';
import { Multilink, Target } from '../types/link.types';
import { Alternate } from '../../../types/alternate';
import { UserType } from '../../../types/user-type';
import { FundApiService } from '../../services/fund-api';

@Component({ tag: 'rcm-navigation', styleUrl: 'rcm-navigation.scss', shadow: true })
export class RcmNavigation {
  private flyout;

  private navigation;

  private buttons;

  searchInput: HTMLInputElement;

  searchIcon: HTMLElement;

  @Element() el: HTMLElement;

  @State() mobileOpen = false;

  @State() searchOpen = false;

  @State() showSearchResults = false;

  @State() openSearchOnRender = false;

  @State() searchValue: string;

  @Prop({ mutable: true }) tabEntries: TabEntry[];

  @Prop() currentLanguage: string;

  @Prop() currentCountry: string;

  @Prop() countryCode: string;

  @Prop() userTypeFromSlug: string;

  @Prop() userType: string;

  @State() userTypeOptions: UserType[];

  @Prop({ attribute: 'tab-entries' }) tempTabEntries: string;

  @Event() toggleSearchEvent: EventEmitter;

  @Event({ eventName: 'searchWeb', composed: true }) searchWeb: EventEmitter;

  @Event() closeSearch: EventEmitter;

  @State() mobileLogoWidth = remCalc(32);

  @Prop() alternates: Alternate[] | string;

  @Prop() secondaryNavigation: string;

  @State() haveShadow = true;

  openedSubmenu = undefined;

  currentUserType = AppService.getUserType();

  globalConfig: GlobalConfig;

  globalContent: GlobalContent;

  isRebranded: boolean;

  tempTabEntriesChangeHandler(newValue: string) {
    const tabEntriesUnfiltered: Record<UserType, TabEntry[]> = newValue ? JSON.parse(newValue) : this.tabEntries;

    this.tabEntries = tabEntriesUnfiltered[this.currentUserType];
  }

  buildAvailableUserTypes() {
    const { homepage } = this.globalContent.navigation;

    this.userTypeOptions = Object.values(UserType).filter((userType) => !!homepage[userType] && !['/', ''].includes(homepage[userType]));
  }

  getLogoLink = () => {
    const homePageLink = StringService.buildHomePageLink();
    const normalizedHomePageLink = StringService.normalizeLink(homePageLink);

    return normalizedHomePageLink;
  };

  @Listen('rcmUserSwitchClose', { target: 'window' })
  componentWillLoad() {
    this.globalConfig = DomService.getGlobalConfig();
    this.globalContent = DomService.getGlobalContent();
    this.isRebranded = this.globalConfig.isRebranded;

    this.buildAvailableUserTypes();
    // for countries without a user type, we default to retail
    this.currentUserType = AppService.getUserType() || UserType.RETAIL;

    this.tempTabEntriesChangeHandler(this.tempTabEntries);
    DomService.fireWhenBelow('medium', () => {
      this.mobileLogoWidth = remCalc(151);
    });
    DomService.fireWhenAboveOrEqual('medium', () => {
      this.mobileLogoWidth = remCalc(226);
    });
  }

  componentWillRender() {
    FundApiService.getBlackoutInformation().then((data) => {
      if (!sessionStorage.getItem('blackout') && data.blackout === 'true') {
        this.createBlackoutModal(true);
      } else {
        this.createBlackoutModal(false);
      }
    });
  }

  @Listen('userSwitchActiveEvent', { target: 'window' })
  updateSearchResultsOpen(e) {
    this.showSearchResults = !e.detail.userSwitcherOpen;
  }

  @Listen('pinningToNavigationEvent', { target: 'window' })
  updateingNavShadow(e) {
    this.haveShadow = !e.detail.pinned;
  }

  @Listen('click', { target: 'document' })
  listenToClickOutsideMenu(event) {
    const target = event.target as HTMLElement;
    const parentNav = this.getParentByAttribute(target, 'id', 'main-navigation');
    if (parentNav === null) {
      this.closeAllSubmenus();
    }
  }

  componentDidLoad() {
    this.navigation = document.querySelector('rcm-navigation');
    this.flyout = this.el.shadowRoot.querySelectorAll('.submenu__container');
    this.buttons = this.el.shadowRoot.querySelectorAll<Element & RcmButton>('rcm-button');

    this.initializeEventhandlers();
  }

  createBlackoutModal(open: boolean) {
    const modal = document.querySelector('.blackout-modal');

    if (!modal) {
      const blackoutModal = document.createElement('oh-modal');
      blackoutModal.className = 'blackout-modal';
      blackoutModal.open = open;
      blackoutModal.extendedWidth = true;
      blackoutModal.headline = DomService.getGlobalContent().blackout.title;
      blackoutModal.subline = DomService.getGlobalContent().blackout.text;
      blackoutModal.actionButtonLabelRight = DomService.getGlobalContent().blackout.buttonText;
      blackoutModal.closeButtonLabel = DomService.getGlobalContent().blackout.closeAriaLabel;
      blackoutModal.actionType = ActionType.ACKNOWLEDGE;

      blackoutModal.style.position = 'fixed';
      blackoutModal.style.top = '0';
      blackoutModal.style.left = '0';
      blackoutModal.style.zIndex = '9999';

      blackoutModal.addEventListener('actionButtonRightClick', () => this.onBlackoutModalClose());
      blackoutModal.addEventListener('close', () => this.onBlackoutModalClose());

      document.body.insertBefore(blackoutModal, document.body.firstChild);
    }
  }

  hideFlyout = () => {
    this.flyout.forEach((f) => {
      // eslint-disable-next-line no-param-reassign
      f.style.display = 'none';
    });
  };

  showFlyout = () => {
    this.flyout.style.display = 'flex';
  };

  initializeEventhandlers() {
    if (DomService.isTouchDevice()) {
      document.addEventListener('touchend', (e) => {
        if (!e.composedPath().includes(this.navigation)) {
          this.hideFlyout();
          this.resetBtn();
        }
      });
    } else {
      document.addEventListener('pointerdown', (e) => {
        if (this.mobileOpen) {
          if (!e.composedPath().includes(this.navigation)) {
            this.mobileOpen = false;
          }
        }
      });
    }
  }

  batchNavigationLinks(links: TabEntry[], parentId: string) {
    const MaxNumberOfLinksInColumn = 8;
    const numberOfColumns = Math.ceil(links.length / MaxNumberOfLinksInColumn);
    const itemsPerColumn = Math.ceil(links.length / numberOfColumns);
    const chunks = [];

    for (let i = 0, len = links.length; i < len; i += itemsPerColumn) {
      chunks.push(links.slice(i, i + itemsPerColumn));
    }

    return (
      <ul class="submenu__container" aria-labelledby={parentId} role="menu">
        {chunks.map(
          (chunk, index) =>
            Array.isArray(chunk) && (
              <div class="submenu" key={chunk[index].href}>
                {chunk.map((child, i) => (
                  <li key={`submenu-${parentId}`} role="menuitem">
                    <rcm-text-link
                      key={child.text}
                      text={child.text}
                      link={{ src: child.href, target: Target.SELF }}
                      onFocusout={(event) => {
                        if (i === chunk.length - 1) {
                          this.focusLossOnSubmenuItem(event);
                        }
                      }}
                    />
                  </li>
                ))}
              </div>
            ),
        )}
      </ul>
    );
  }

  resetBtn = () => {
    this.buttons.forEach((btn) => {
      // eslint-disable-next-line no-param-reassign
      btn.hovered = false;
    });
  };

  showSubmenu(submenu: HTMLElement, button: HTMLRcmButtonElement) {
    // eslint-disable-next-line no-param-reassign
    submenu.style.display = 'flex';
    // eslint-disable-next-line no-param-reassign
    button.expanded = true;
  }

  hideSubmenu(submenu: HTMLElement, button: HTMLRcmButtonElement) {
    // eslint-disable-next-line no-param-reassign
    submenu.style.display = 'none';
    // eslint-disable-next-line no-param-reassign
    button.expanded = false;
  }

  toggleDesktopSubmenu(event: Event, parentId: string) {
    const targetElement = event.target as HTMLRcmButtonElement;
    const parent: HTMLElement = this.getParentByAttribute(targetElement, 'class', 'navigation__container');
    parent.parentElement.querySelectorAll('.submenu__container').forEach((element) => {
      if (element.previousElementSibling !== targetElement) {
        this.hideSubmenu(element as HTMLElement, element.previousElementSibling as HTMLRcmButtonElement);
      }
    });
    const submenu: HTMLElement = parent.querySelector(`[aria-labelledby="${parentId}"]`);
    if (submenu.style.display !== 'flex') {
      submenu.style.display = 'flex';
      targetElement.expanded = true;
      this.showSubmenu(submenu, targetElement);
    } else {
      this.hideSubmenu(submenu, targetElement);
    }
  }

  getParentByAttribute(element: HTMLElement, attributeName: string, attributeValue: string) {
    let parent = element.parentElement;
    while (parent) {
      if (parent.getAttribute(attributeName) === attributeValue) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  getAllShadowRoots(root = document): HTMLElement[] {
    const shadowRoots = [];

    function traverse(node) {
      if (node.shadowRoot) {
        shadowRoots.push(node.shadowRoot);
      }

      node.childNodes.forEach((child) => traverse(child));
    }

    traverse(root);
    return shadowRoots as HTMLElement[];
  }

  focusLossOnSubmenuItem(event: FocusEvent) {
    const nowFocusedTarget = event.relatedTarget as HTMLElement;
    const parentOfNowFocusedTarget = this.getParentByAttribute(nowFocusedTarget, 'class', 'submenu__container');
    if (parentOfNowFocusedTarget !== null) {
      const target = event.target as HTMLElement;
      const parent: HTMLElement = this.getParentByAttribute(target, 'class', 'submenu__container');
      if (!(parent.previousElementSibling as HTMLRcmButtonElement).expanded) {
        parent.querySelectorAll('.submenu__container').forEach((element) => {
          this.hideSubmenu(element as HTMLElement, element.previousElementSibling as HTMLRcmButtonElement);
        });
      }
    } else {
      this.closeAllSubmenus();
    }
  }

  closeAllSubmenus(event?: Event) {
    if (event) {
      const target = event.target as HTMLElement;
      if ((target.shadowRoot.firstChild as HTMLRcmButtonElement).expanded) {
        return;
      }
    }
    this.getAllShadowRoots().forEach((shadowRoot: HTMLElement) => {
      const submenus = shadowRoot.querySelectorAll('.submenu__container');
      submenus.forEach((submenu) => this.hideSubmenu(submenu as HTMLElement, submenu.previousElementSibling as HTMLRcmButtonElement));
    });
  }

  renderTabs = () =>
    this.tabEntries?.slice(0, 3).map((entry) => {
      if (entry.text !== '' && entry.href !== '') {
        const itemId = entry.text.replace(' ', '-').toLowerCase();
        return (
          <li key={entry.href} role="menuitem">
            <rcm-button
              onRcmButtonClick={(event) => this.toggleDesktopSubmenu(event, itemId)}
              onFocus={(event) => this.closeAllSubmenus(event)}
              key={entry.text}
              text={entry.text}
              appearance={ButtonAppearance.NAVIGATION_TAB}
              parentComponent="rcm-navigation"
              buttonId={itemId}
              expanded={false}
              {...(entry.children?.length > 0 ? { ariaLabel: `${entry.text} ${this.globalContent.navigation.hasSubmenu.label}` } : {})}
            ></rcm-button>
            {entry.children?.length > 0 && this.batchNavigationLinks(entry.children, itemId)}
          </li>
        );
      }
      return '';
    });

  toggleMobileMenu() {
    this.mobileOpen = !this.mobileOpen;
    if (this.searchOpen) {
      this.toggleSearch();
    }
  }

  toggleSearch() {
    if (this.searchOpen) {
      this.handleCloseSearch();
      this.searchOpen = false;
    } else if (this.mobileOpen) {
      this.mobileOpen = false;
      this.openSearchOnRender = true;
    } else {
      this.searchOpen = true;
    }
    this.toggleSearchEvent.emit({ searchOpen: this.searchOpen });
  }

  handleCloseSearch() {
    this.searchOpen = false;
    this.showSearchResults = false;
    this.searchValue = '';
    this.searchInput.value = '';
    this.toggleSearchEvent.emit({ searchOpen: this.searchOpen });
    document.querySelector('body').style.overflowY = 'auto';
  }

  handleSearch() {
    this.showSearchResults = true;
    this.searchValue = this.searchInput.value;
    this.searchInput.blur();
    document.querySelector('body').style.overflowY = 'hidden';
  }

  handleSearchButtonKeyDown(e) {
    if (e.keyCode === 13) {
      this.handleSearch();
    }
  }

  closeMobileSubmenu(button: HTMLRcmButtonElement) {
    const linkContainer = button.parentElement.querySelector<HTMLElement>('.mobile-container__links');

    // eslint-disable-next-line no-param-reassign
    button.appearance = ButtonAppearance.MOBILE_SUBMENU_CLOSED;
    linkContainer.style.display = 'none';
    this.openedSubmenu = undefined;
  }

  openMobileSubmenu(button: HTMLRcmButtonElement) {
    const linkContainer = button.parentElement.querySelector<HTMLElement>('.mobile-container__links');

    // eslint-disable-next-line no-param-reassign
    button.appearance = ButtonAppearance.MOBILE_SUBMENU_OPEN;
    linkContainer.style.display = 'flex';
    this.openedSubmenu = button;
    (linkContainer.firstChild as HTMLElement).focus();
  }

  closeAllMobileSubmenus() {
    const submenuButtons = this.el.shadowRoot.querySelectorAll<HTMLElement>('.mobile-submenu__button');
    submenuButtons.forEach((button: HTMLRcmButtonElement) => this.closeMobileSubmenu(button));
  }

  toggleSubmenu(eventTarget: HTMLRcmButtonElement) {
    if (this.openedSubmenu === eventTarget) {
      this.closeMobileSubmenu(eventTarget);
      // eslint-disable-next-line no-param-reassign
      eventTarget.expanded = false;
    } else {
      this.closeAllMobileSubmenus();
      this.openMobileSubmenu(eventTarget);
      // eslint-disable-next-line no-param-reassign
      eventTarget.expanded = true;
    }
  }

  handleTopMobileMenuFocusOut(target: FocusEvent) {
    console.log(target);
    const relatedTargetElement = target.relatedTarget as HTMLElement;
    console.log(relatedTargetElement?.getAttribute('id'));
    if (relatedTargetElement?.getAttribute('id') === 'close-mobile-menu-button') {
      this.closeAllMobileSubmenus();
    }
  }

  handleMobileMenuFocusOut(target: FocusEvent) {
    const targetElement = target.target as HTMLElement;
    const parent = this.getParentByAttribute(targetElement, 'role', 'region');
    if (target.relatedTarget !== null) {
      const relatedTargetParent = this.getParentByAttribute(target.relatedTarget as HTMLElement, 'role', 'region');
      if (parent !== relatedTargetParent) {
        this.closeAllMobileSubmenus();
      }
    }
  }

  renderLoginButton(label: string, href: string, loginInNewTab: string = '0') {
    const link: Multilink = { src: href, target: loginInNewTab === '1' ? Target.BLANK : Target.SELF };

    return (
      label !== '' &&
      href !== '' && (
        <rcm-wrapper-link link={link} trackingLinkText={label} parentComponent="rcm-navigation">
          <div class="navigation__button">
            <div class="navigation__button__icon">
              <rcm-icon icon={'lock'} height={`${remCalc(16)}rem`} width={`${remCalc(16)}rem`} color="var(--color-dark-base)"></rcm-icon>
            </div>
            <div class="text navigation__button__text">{label}</div>
          </div>
        </rcm-wrapper-link>
      )
    );
  }

  renderMobileLinks() {
    if (this.mobileOpen) {
      document.body.style.overflow = 'hidden';
      const { label, href, iconButton, openInNewTab } = DomService.getGlobalContent().navigation?.cta?.[this.currentUserType];
      const openNavButtonInNewTab = openInNewTab === '1' ? Target.BLANK : Target.SELF;

      return (
        <div class="mobile-container" style={{ overflowY: 'scroll' }} id="mobile-menu">
          <div class="mobile-container__items">
            {this.tabEntries?.map((entry, index) => (
              <div class="mobile-container__item" key={entry.href}>
                <rcm-button
                  class="close mobile-submenu__button"
                  appearance={ButtonAppearance.MOBILE_SUBMENU_CLOSED}
                  text={entry.text}
                  onRcmButtonClick={(e) => this.toggleSubmenu(e.target)}
                  buttonId={`mobile-submenu-${index}`}
                  onFocusout={(e) => this.handleTopMobileMenuFocusOut(e)}
                  controls={`mobile-submenu-${index}-container`}
                  expanded={false}
                />
                <div
                  class="mobile-container__links"
                  id={`mobile-submenu-${index}-container`}
                  role="region"
                  aria-labelledby={`mobile-submenu-${index}`}
                >
                  {entry.children?.map((c) => (
                    <rcm-text-link key={c.href} link={c.href} text={c.text} onFocusout={(e) => this.handleMobileMenuFocusOut(e)} />
                  ))}
                </div>
              </div>
            ))}
            {label !== '' &&
              href !== '' &&
              (iconButton === '1' ? (
                this.renderLoginButton(label, href, openNavButtonInNewTab)
              ) : (
                <rcm-button
                  text={label}
                  appearance={ButtonAppearance.PRIMARY_YELLOW}
                  link={{ src: href, target: openNavButtonInNewTab }}
                  parentComponent="rcm-navigation"
                ></rcm-button>
              ))}

            <rcm-secondary-navigation secondaryNavigationItems={this.secondaryNavigation} countryCode={this.countryCode}></rcm-secondary-navigation>
          </div>
          <div class="mobile-container__bottom-nav">
            <rcm-country-switcher
              country-value={this.globalConfig.country}
              country-label={this.globalContent.countryNames?.[this.globalConfig.country]}
            ></rcm-country-switcher>
            {this.isRebranded ? (
              <rcm-user-dropdown-switcher userTypeOptions={this.userTypeOptions}></rcm-user-dropdown-switcher>
            ) : (
              <rcm-user-switcher userTypeOptions={this.userTypeOptions}></rcm-user-switcher>
            )}
            <rcm-language-switcher
              class="navigation__language-switch"
              currentCountry={this.currentCountry}
              currentLanguage={this.currentLanguage}
              alternates={this.alternates}
            ></rcm-language-switcher>
          </div>
        </div>
      );
    }
    document.body.style.overflow = !document.getElementById('scrolly-page') ? 'initial' : 'hidden';
    return null;
  }

  onBlackoutModalClose() {
    const modal = document.querySelector('.blackout-modal') as HTMLElement & { open: string };
    if (modal) {
      modal.open = 'false';
    }

    sessionStorage.setItem('blackout', 'read');
  }

  componentDidRender() {
    if (this.openSearchOnRender) {
      // workaround to set the search input focus when opening search while mobile nav is open
      // click event neeed to be fired due to Safari blocking programmatic focus
      this.searchIcon.click();
      this.openSearchOnRender = false;
    }
    if (this.searchOpen) {
      this.searchInput.focus();
    }
  }

  getLoginButtonInfo(): { label: string; href: string; iconButton: string } {
    const userTypeSpecificData = this.globalContent.navigation?.cta?.[this.currentUserType];
    if (userTypeSpecificData) {
      return { label: userTypeSpecificData.label ?? '', href: userTypeSpecificData.href ?? '', iconButton: userTypeSpecificData.iconButton ?? '' };
    }

    return { label: '', href: '', iconButton: '' };
  }

  public render() {
    const { label, href, iconButton, openInNewTab } = DomService.getGlobalContent().navigation?.cta?.[this.currentUserType];
    const { logo40years, navigation } = DomService.getGlobalContent();
    const logoUrl = StringService.getLogoUrlForCurrentDomainAndUserType();
    const logoAriaLabel = navigation.logo.label;
    const openNavButtonInNewTab = openInNewTab === '1' ? Target.BLANK : Target.SELF;

    return (
      <Fragment>
        <div class="navigation">
          <div class="navigation__pre">
            <rcm-container class="navigation__grid">
              <div class="navigation__pre-container">
                <div class="navigation__group">
                  <rcm-country-switcher
                    class="navigation_country-switch"
                    country-value={this.globalConfig.country}
                    country-label={this.globalContent.countryNames?.[this.globalConfig.country]}
                  ></rcm-country-switcher>

                  {this.isRebranded ? (
                    <rcm-user-dropdown-switcher userTypeOptions={this.userTypeOptions}></rcm-user-dropdown-switcher>
                  ) : (
                    <rcm-user-switcher userTypeOptions={this.userTypeOptions}></rcm-user-switcher>
                  )}
                </div>

                <div class="navigation__group">
                  <rcm-secondary-navigation
                    secondaryNavigationItems={this.secondaryNavigation}
                    countryCode={this.countryCode}
                  ></rcm-secondary-navigation>

                  <rcm-language-switcher
                    class="navigation__language-switch"
                    currentCountry={this.currentCountry}
                    currentLanguage={this.currentLanguage}
                    alternates={this.alternates}
                  ></rcm-language-switcher>
                </div>
              </div>
            </rcm-container>
          </div>
          <div class={{ navigation__main: true, navShadow: this.haveShadow && !this.searchOpen }}>
            <rcm-container class="navigation__grid">
              <nav class="navigation__container" role="menubar">
                <rcm-wrapper-link link={this.getLogoLink()} parentComponent="rcm-navigation" ariaLabel={logoAriaLabel}>
                  <img class="navigation__logo" src={logoUrl} style={{ height: `${remCalc(40)}rem` }} alt={logoAriaLabel} />
                </rcm-wrapper-link>
                {StringService.isUrl(logo40years.url) && (
                  <rcm-wrapper-link
                    link={{ src: logo40years.redirectUrl, target: logo40years.openInNewTab === '1' ? Target.BLANK : Target.SELF }}
                    ariaLabel={logo40years.redirectAriaLabel}
                  >
                    <img class="navigation__logo-40-years" src={logo40years.url} alt={logo40years.alt} />
                  </rcm-wrapper-link>
                )}
                <ul id="main-navigation">{this.renderTabs()}</ul>
                <div class="navigation__right">
                  {iconButton === '1'
                    ? this.renderLoginButton(label, href, openNavButtonInNewTab)
                    : label !== '' &&
                      href !== '' && (
                        <rcm-button
                          text={label}
                          appearance={ButtonAppearance.PRIMARY_YELLOW}
                          link={{ src: href, target: openNavButtonInNewTab }}
                          parentComponent="rcm-navigation"
                          key={`navigation-button-${label.replace(/\s/g, '')}`}
                        ></rcm-button>
                      )}
                  {![...RCM_NO_USER_TYPE_COUNTRIES, 'not'].includes(this.currentCountry) && (
                    <rcm-button
                      ref={(el) => {
                        if (el !== null) {
                          this.searchIcon = el as HTMLElement;
                        }
                      }}
                      class="search-icon"
                      appearance={ButtonAppearance.GLOBAL_SEARCH}
                      onRcmButtonClick={() => this.toggleSearch()}
                      ariaLabel={DomService.getGlobalContent().webSearch.open}
                      expanded={this.searchOpen}
                      controls={'global-search'}
                    ></rcm-button>
                  )}
                </div>
              </nav>
            </rcm-container>
          </div>
          <div class="navigation--mobile">
            <div class="navigation--mobile__logo-container">
              <rcm-wrapper-link link={this.getLogoLink()} parentComponent="rcm-navigation" ariaLabel={logoAriaLabel}>
                <img class="navigation__logo" src={logoUrl} style={{ height: `${remCalc(40)}rem` }} alt={logoAriaLabel} />
              </rcm-wrapper-link>
              {StringService.isUrl(logo40years.url) && (
                <rcm-wrapper-link
                  class="navigation__logo-40-years"
                  link={{ src: logo40years.redirectUrl, target: logo40years.openInNewTab === '1' ? Target.BLANK : Target.SELF }}
                  ariaLabel={logo40years.redirectAriaLabel}
                >
                  <img src={logo40years.url} alt={logo40years.alt} />
                </rcm-wrapper-link>
              )}
            </div>
            <div class="navigation--mobile__icons">
              {![...RCM_NO_USER_TYPE_COUNTRIES, 'not'].includes(this.currentCountry) && (
                <rcm-button
                  class="navigation--mobile__search-icon"
                  appearance={ButtonAppearance.GLOBAL_SEARCH}
                  onRcmButtonClick={() => this.toggleSearch()}
                  ariaLabel={DomService.getGlobalContent().webSearch.open}
                  expanded={this.searchOpen}
                  controls={'global-search'}
                ></rcm-button>
              )}
              <rcm-button
                onRcmButtonClick={() => this.toggleMobileMenu()}
                class={!this.mobileOpen ? 'open' : 'close'}
                icon={!this.mobileOpen ? 'menu' : 'x'}
                iconSize={26}
                appearance={ButtonAppearance.DEFAULT}
                expanded={this.mobileOpen}
                controls="mobile-menu"
                id={'close-mobile-menu-button'}
              />
            </div>
          </div>
          {this.renderMobileLinks()}
          <div
            class={{ navigation__sub: true, 'navigation__sub--hidden': !this.searchOpen }}
            id="global-search"
            hidden={!this.searchOpen}
            role="search"
            aria-label={DomService.getGlobalContent().webSearch.region}
          >
            {this.searchOpen && (
              <rcm-container class="navigation__search">
                <rcm-columns spanMobile>
                  <rcm-column span={10}>
                    <input
                      type="search"
                      ref={(el) => {
                        if (el !== null) {
                          this.searchInput = el as HTMLInputElement;
                        }
                      }}
                      onInput={() => {
                        this.searchValue = this.searchInput.value;
                      }}
                      onKeyDown={(e) => this.handleSearchButtonKeyDown(e)}
                      placeholder={DomService.getGlobalContent().webSearch.placeholder}
                    ></input>
                  </rcm-column>
                  <rcm-column span={2}>
                    <rcm-button
                      icon="search"
                      class="search__button-desktop"
                      theme={ButtonTheme.LIGHT}
                      text={DomService.getGlobalContent().webSearch?.searchLabel}
                      ariaLabel={DomService.getGlobalContent().webSearch?.searchLabel}
                      onRcmButtonClick={() => this.handleSearch()}
                      disabled={!this.searchValue}
                    />
                    <rcm-button
                      icon="search"
                      class="search__button-mobile"
                      theme={ButtonTheme.LIGHT}
                      ariaLabel={DomService.getGlobalContent().webSearch?.searchLabel}
                      onRcmButtonClick={() => this.handleSearch()}
                      disabled={!this.searchValue}
                    />
                    <rcm-button
                      appearance={ButtonAppearance.MODAL_CLOSE}
                      onRcmButtonClick={() => this.handleCloseSearch()}
                      ariaLabel={DomService.getGlobalContent().webSearch?.close}
                    ></rcm-button>
                  </rcm-column>
                </rcm-columns>
              </rcm-container>
            )}
          </div>
        </div>
        {this.showSearchResults && <rcm-web-search-results value={this.searchValue} show={this.showSearchResults}></rcm-web-search-results>}
      </Fragment>
    );
  }
}
